/**
 * Prospect-facing pitch page rendered at /proposal/{slug}.
 *
 * Two flavours:
 *   - social_pitch (existing) → renders the full LandingPage with bespoke prices
 *   - new_business (new)     → renders NewBusinessProposalView (full deck)
 *
 * Behind the scenes: fetches the proposal from the public slug endpoint
 * (no auth — slug is the only secret) then renders the appropriate view.
 *
 * View tracking:
 *   - Each browser gets a stable `visitor_id` in localStorage (lifelong)
 *   - Each tab gets a `session_id` in sessionStorage (per visit)
 *   - These travel as query params on the public fetch so the server can
 *     bucket repeated reloads vs distinct opens
 *   - Time-on-page is heart-beated to /heartbeat on visibilitychange + unload
 */
import { useEffect, useState } from "react";
import axios from "axios";
import NewBusinessProposalView from "@/NewBusinessProposalView";
import SocialProposalView from "@/SocialProposalView";

const API = process.env.REACT_APP_BACKEND_URL;

const ensureVisitorId = () => {
  try {
    let id = window.localStorage.getItem("nc_visitor_id");
    if (!id) {
      id = (window.crypto?.randomUUID?.() || `v-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
      window.localStorage.setItem("nc_visitor_id", id);
    }
    return id;
  } catch {
    return null;
  }
};

const ensureSessionId = () => {
  try {
    let id = window.sessionStorage.getItem("nc_session_id");
    if (!id) {
      id = (window.crypto?.randomUUID?.() || `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
      window.sessionStorage.setItem("nc_session_id", id);
    }
    return id;
  } catch {
    return null;
  }
};

export default function ProposalPublicView({ slug }) {
  const [proposal, setProposal] = useState(null);
  const [catalogue, setCatalogue] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | not_found

  useEffect(() => {
    if (!slug) {
      setStatus("not_found");
      return;
    }
    const visitor_id = ensureVisitorId();
    const session_id = ensureSessionId();
    let viewId = null;
    let openedAt = Date.now();
    let heartbeatTimer = null;

    const sendHeartbeat = (final = false) => {
      if (!viewId) return;
      const seconds = Math.round((Date.now() - openedAt) / 1000);
      const url = `${API}/api/proposals/public/${slug}/heartbeat`;
      const body = JSON.stringify({ view_id: viewId, seconds });
      // sendBeacon is the only reliable way during unload
      if (final && navigator.sendBeacon) {
        try {
          navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
          return;
        } catch { /* fall through */ }
      }
      // Fire and forget; ignore errors (prospect's page must not block on this)
      try {
        axios.post(url, { view_id: viewId, seconds }, { headers: { "Content-Type": "application/json" } });
      } catch { /* noop */ }
    };

    const onVisibility = () => { if (document.visibilityState === "hidden") sendHeartbeat(false); };
    const onUnload = () => sendHeartbeat(true);

    (async () => {
      try {
        const r = await axios.get(`${API}/api/proposals/public/${slug}`, {
          params: { visitor_id, session_id },
        });
        setProposal(r.data);
        viewId = r.data.view_id || null;
        // Periodic heartbeat every 15s so we still get time-on-page if the
        // user just leaves the tab open (no unload event fires).
        if (viewId) {
          heartbeatTimer = setInterval(() => sendHeartbeat(false), 15000);
          document.addEventListener("visibilitychange", onVisibility);
          window.addEventListener("pagehide", onUnload);
          window.addEventListener("beforeunload", onUnload);
        }
        if (r.data.proposal_type === "new_business") {
          try {
            const c = await axios.get(`${API}/api/proposals/public/catalogue`);
            setCatalogue(c.data);
          } catch { /* best effort */ }
        }
        setStatus("ok");
      } catch (err) {
        setStatus(err?.response?.status === 410 ? "expired" : "not_found");
      }
    })();

    return () => {
      if (heartbeatTimer) clearInterval(heartbeatTimer);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onUnload);
      window.removeEventListener("beforeunload", onUnload);
      sendHeartbeat(true);
    };
  }, [slug]);

  if (status === "loading") {
    return (
      <div className="ppv-state">
        <div className="ppv-spinner" />
        <style>{ppvStyles}</style>
      </div>
    );
  }

  if (status === "expired") {
    return (
      <div className="ppv-state" data-testid="ppv-expired">
        <div className="ppv-error">
          <h1>This proposal link has expired</h1>
          <p>
            Proposal links stay live for a limited time. Your NicheCom account manager can
            refresh it in a moment — just ask and we'll send you a fresh link.
          </p>
          <a href="/" className="ppv-home-link">← Back to nichesocial.app</a>
        </div>
        <style>{ppvStyles}</style>
      </div>
    );
  }

  if (status === "not_found") {
    return (
      <div className="ppv-state">
        <div className="ppv-error">
          <h1>Proposal not found</h1>
          <p>
            The link may have expired or been deleted. If your account manager
            sent it recently, ask them to resend.
          </p>
          <a href="/" className="ppv-home-link">← Back to nichesocial.app</a>
        </div>
        <style>{ppvStyles}</style>
      </div>
    );
  }

  if (proposal.proposal_type === "new_business") {
    return <NewBusinessProposalView proposal={proposal} catalogue={catalogue} />;
  }

  // Default: pricing table + example video carousel (SocialProposalView).
  return <SocialProposalView proposal={proposal} />;
}

const ppvStyles = `
  .ppv-state {
    min-height: 100vh;
    background: #0a0d14;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
  }
  .ppv-error { max-width: 480px; text-align: center; }
  .ppv-error h1 {
    font-size: 28px;
    font-weight: 800;
    letter-spacing: -0.01em;
    margin: 0 0 12px;
  }
  .ppv-error p {
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.6;
    margin: 0 0 20px;
  }
  .ppv-home-link {
    color: #96C11F;
    text-decoration: none;
    font-weight: 600;
  }
  .ppv-home-link:hover { text-decoration: underline; }
  .ppv-spinner {
    width: 36px;
    height: 36px;
    border: 3px solid rgba(255, 255, 255, 0.12);
    border-top-color: #96C11F;
    border-radius: 50%;
    animation: ppv-rot 0.8s linear infinite;
  }
  @keyframes ppv-rot { to { transform: rotate(360deg); } }
`;
